import { useCallback, useMemo } from 'react'

import { useGetAuroraCareOptionsModalContent } from './useGetCareOptionsModalContent'
import { AuroraCareOption } from '../careOptionsMetadata/auroraCareOptionsMetadata'
import { ProgramDetailsContent } from '../organisms/programDetailsModal/ProgramDetailsModal'
import { CareOptionAvailabilityInfo } from '../utils/careOptions/types'
import { getAvailableCareOptions } from '../utils/careOptions/utils'

export interface CareOptionsGridPressHandlers {
  onSelfTriagePress: () => void
  onChildTriagePress: () => void
  onCoachingPress: () => void
  onParentingCoachingPress: () => void
  onCouplesTherapyPress: () => void
  onFamilyTherapyPress: () => void
  onMedicationConsultationPress: () => void
  onOnsiteTherapyPress: () => void
}

interface UseCareOptionsGridPropsArgs {
  careOptionAvailabilityInfo: CareOptionAvailabilityInfo
  pressHandlers: CareOptionsGridPressHandlers
  isDirectAccessMedsSupported: boolean
  isBlendedCareMedsAvailable?: boolean
  onContinuePress?: () => void
  customerName?: string
}

/**
 * Assembles the props for `CareOptionsGrid` (Forked Triage v3) from a customer's availability
 * info and the app's navigation handlers. Shared by the web and mobile Forked Triage containers so
 * the care-option → action mapping lives in one place.
 */
export const useCareOptionsGridProps = ({
  careOptionAvailabilityInfo,
  pressHandlers,
  isDirectAccessMedsSupported,
  isBlendedCareMedsAvailable,
  onContinuePress,
  customerName,
}: UseCareOptionsGridPropsArgs) => {
  const modalContentByOption = useGetAuroraCareOptionsModalContent({
    isDirectAccessMedsSupported,
    isBlendedCareMedsAvailable,
    onContinuePress,
  }) as Partial<Record<AuroraCareOption, ProgramDetailsContent>>

  const careOptions = useMemo(() => getAvailableCareOptions(careOptionAvailabilityInfo), [careOptionAvailabilityInfo])

  const onCareOptionPress = useCallback(
    (careOption: AuroraCareOption) => {
      const pressByOption: Partial<Record<AuroraCareOption, () => void>> = {
        [AuroraCareOption.SELF_TRIAGE]: pressHandlers.onSelfTriagePress,
        [AuroraCareOption.CHILD_TRIAGE]: pressHandlers.onChildTriagePress,
        [AuroraCareOption.MENTAL_HEALTH_COACHING]: pressHandlers.onCoachingPress,
        [AuroraCareOption.PARENTING_COACHING]: pressHandlers.onParentingCoachingPress,
        [AuroraCareOption.COUPLES_THERAPY]: pressHandlers.onCouplesTherapyPress,
        [AuroraCareOption.FAMILY_THERAPY]: pressHandlers.onFamilyTherapyPress,
        [AuroraCareOption.MEDICATION_CONSULTATION]: pressHandlers.onMedicationConsultationPress,
        [AuroraCareOption.ONSITE_THERAPY]: pressHandlers.onOnsiteTherapyPress,
      }
      pressByOption[careOption]?.()
    },
    [pressHandlers],
  )

  const formattedMessageValuesByOption = useMemo(
    () => ({ [AuroraCareOption.ONSITE_THERAPY]: { company: customerName } }),
    [customerName],
  )

  return { careOptions, onCareOptionPress, modalContentByOption, formattedMessageValuesByOption }
}
